import { Injectable } from '@angular/core';
import { AiToolForm } from '../forms';

/**
 * Ensures that only one form is created for each tool
 */
@Injectable()
export class AiToolFormRegistryService {
  // Map of tool ID to form instance
  private readonly toolFormRegistry = new Map<string, AiToolForm>();

  /**
   * Gets an existing tool form or registers a new one if not found
   */
  getOrRegister(toolId: string, formFactory: () => AiToolForm): AiToolForm {
    if (!toolId) {
      // For new tools without IDs, always create a new form
      const f = formFactory();
      return f;
    }

    // Check if we already have a form for this tool
    const existingForm = this.toolFormRegistry.get(toolId);
    if (existingForm) {
      // Return a clone to avoid direct modification of the registry entry
      const f = existingForm;
      return f;
    }

    // Create and register a new form
    const newForm = formFactory();
    this.toolFormRegistry.set(toolId, newForm);
    return newForm;
  }

  /**
   * Updates the registered form for a tool ID
   */
  updateRegistry(toolId: string, form: AiToolForm): void {
    if (!toolId) return;
    this.toolFormRegistry.set(toolId, form);
  }

  /**
   * Checks if a tool form is registered
   */
  hasRegisteredForm(toolId: string): boolean {
    return this.toolFormRegistry.has(toolId);
  }

  /**
   * Clears registry entries that are no longer needed
   * @param activeToolIds Set of tool IDs that are still active
   */
  cleanupRegistry(activeToolIds: Set<string>): void {
    // Remove any forms for tools that are no longer used
    for (const toolId of this.toolFormRegistry.keys()) {
      if (!activeToolIds.has(toolId)) {
        this.toolFormRegistry.delete(toolId);
      }
    }
  }
}
